import React from 'react';
import { Tag, Row } from 'antd';
import { uploadFileToS3 } from '../../../shared/upload/uploadFileToS3';
import { getFileType } from '../../../../core/utils/constants/constants';
import { startCase } from 'lodash';
import { urgencyColors as configUrgencyColors } from '../configs/task.config';

export const getNumberWithSuffix = (number) => {
  // Input validation
  if (!Number.isInteger(number)) {
    throw new Error('Input must be an integer');
  }
  // Handle special cases for 11, 12, 13
  const specialCase = number % 100;
  if (specialCase >= 11 && specialCase <= 13) {
    return `${number}th `;
  }
  // Handle general cases
  switch (number % 10) {
    case 1:
      return `${number}st `;
    case 2:
      return `${number}nd `;
    case 3:
      return `${number}rd `;
    default:
      return `${number}th `;
  }
};

export const linkify = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.split(urlRegex).map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a
          href={part}
          key={index}
          style={{ color: '#007bff', textDecoration: 'underline' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

export const codeBlock = (text) => {
  const parts = [];
  const splitRegex = /(```[\s\S]*?```)/g;
  const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;

  // Split text into normal text and code blocks
  const textParts = text.split(splitRegex);

  textParts.forEach((part) => {
    if (part.startsWith('```')) {
      const match = codeBlockRegex.exec(part);
      if (match) {
        const [, lang, code] = match;
        parts.push({
          type: 'code',
          content: code.trim(),
          language: lang || 'plaintext',
        });
      }
      codeBlockRegex.lastIndex = 0;
    } else if (part) {
      parts.push({ type: 'text', content: part });
    }
  });

  return parts;
};

export const handleUploadFilesToS3 = async (files) => {
  try {
    const medias = [];
    if (files.length) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const media = await uploadFileToS3({ file });
        medias.push({
          ...media,
          fileType: getFileType(file.name),
        });
      }
    }
    return medias;
  } catch (error) {
    console.error(error);
    throw new Error('No greater than 2MB is allowed');
  }
};

export const getLastCommentUser = (record) => {
  if (!record.attachedComments || record.attachedComments.length === 0) return null;
  const sortedComments = [...record.attachedComments].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
  return sortedComments.length > 0 ? sortedComments[0].createdBy : null;
};

export const isUserAssignee = (user, record, includeThirdParty = false) => {
  if (!user || !record) return false;

  const isAssignee =
    (record.assigneeUser && record.assigneeUser.id === user.id) ||
    (record.assigneeUsers && record.assigneeUsers.some((assignee) => assignee.id === user.id));

  const isRequestor = record.createdBy && user.id === record.createdBy.id;

  if (includeThirdParty) {
    const lastCommentUser = getLastCommentUser(record);
    const isThirdPartyComment =
      lastCommentUser &&
      lastCommentUser.id !== (record.createdBy && record.createdBy.id) &&
      !isAssignee;

    return isThirdPartyComment || (isAssignee && !isRequestor);
  }

  return isAssignee && !isRequestor;
};

export const deduplicateUsers = (users) => {
  const userMap = new Map();
  users.forEach((user) => {
    const displayName = startCase(user.name || user.username);
    if (!userMap.has(displayName)) {
      userMap.set(displayName, user);
    }
  });
  return Array.from(userMap.values());
};

export const copyTaskLink = (taskId, messageApi) => {
  const baseUrl = window.location.origin;
  const taskUrl = `${baseUrl}/tasks/details/${taskId}`;

  navigator.clipboard.writeText(taskUrl).then(
    () => {
      messageApi.success('Link copied to clipboard!');
    },
    () => {
      messageApi.error('Failed to copy link');
    }
  );
};

// special case for task status tags
const statusTagConfig = {
  NOT_STARTED: { bg: '#f5f5f5', color: '#8c8c8c', text: 'Not Started' },
  TRIAGED: { bg: '#e6f7ff', color: '#1890ff', text: 'Triaged' },
  READY: { bg: '#e6fffb', color: '#13c2c2', text: 'Ready' },
  IN_PROGRESS: { bg: '#e6f7ff', color: '#1890ff', text: 'In Progress' },
  BLOCKED: { bg: '#fff1f0', color: '#ff4d4f', text: 'Blocked' },
  ON_HOLD: { bg: '#fff7e6', color: '#faad14', text: 'On Hold' },
  COMPLETED: { bg: '#f6ffed', color: '#52c41a', text: 'Completed' },
  MONITORING: { bg: '#f9f0ff', color: '#722ed1', text: 'Monitoring' },
  DUPLICATE: { bg: '#f5f5f5', color: '#8c8c8c', text: 'Duplicate' },
  INPUT_REQUIRED: { bg: '#fff7e6', color: '#fa8c16', text: 'Input Required' },
  NO_LONGER_NEEDED: { bg: '#f5f5f5', color: '#8c8c8c', text: 'Not Needed' },
};

export const TaskStatusTag = ({ status }) => {
  const config = statusTagConfig[status] || { bg: '#f5f5f5', color: '#8c8c8c', text: 'Unknown' };

  return (
    <Tag
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: 0,
        borderRadius: '25px',
        padding: '2px 12px',
        fontSize: '12px',
        fontWeight: '500',
      }}
    >
      <Row justify="space-around" type="flex" align="middle" style={{ gap: 6 }}>
        <div
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: config.color,
          }}
        />
        {config.text}
      </Row>
    </Tag>
  );
};

export const urgencyColors = configUrgencyColors;

// Feature check utilities
export const hasUserCampaignsFeature = (currentUser) => {
  return currentUser?.features?.includes('CAMPAIGNS');
};

export const hasOrgCampaignsFeature = (currentUser) => {
  return currentUser?.role?.roleItems?.some(item => item.feature === 'CAMPAIGNS');
};

export const hasCampaignsFeature = (currentUser) => {
  return hasUserCampaignsFeature(currentUser) && hasOrgCampaignsFeature(currentUser);
};
export const getResponsiveBreakpoints = (windowWidth) => ({
  isMobile: windowWidth < 768,
  isTablet: windowWidth >= 768 && windowWidth < 1024,
  isDesktop: windowWidth >= 1024,
});
